import { BASELINE_WINDOW_MIN, FRICTION_TYPES, R_LINK_MIN, RESCUES, T_RESOLVE_MIN, W_LOOKBACK_MIN } from '../constants';
import type { FrictionSubtype, InterruptionSource, Ping, PingType, RescueKind, RescueSession } from '../types';

export interface HeatCell {
  dow: number;
  hour: number;
  count: number;
}

export interface TransitionRow {
  from: PingType;
  to: PingType;
  count: number;
  medianGapMin: number;
}

export interface RescueEffectiveness {
  kind: RescueKind;
  startedCount: number;
  completedCount: number;
  completionRate: number;
  resolvedRate: number;
  outcomeLoggedCount: number;
  outcomeHelpedCount: number;
  helpfulRate: number | null;
  medianTimeToNextPingMin: number | null;
  smallSample: boolean;
}

export interface TinyWinSummary {
  completedRescues: number;
  withTinyWin: number;
  captureRate: number;
  latest: string[];
}

export interface PrepChecklistSummary {
  timedRescues: number;
  phoneSilencedRate: number;
  tabsClosedRate: number;
  docOpenRate: number;
  timerVisibleRate: number;
}

export interface FrictionStartSummary {
  withStartCount: number;
  medianLeadMin: number | null;
  meanLeadMin: number | null;
}

function median(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

export function computeHeatmap(pings: Ping[]): { cells: HeatCell[]; topTypes: Array<{ type: PingType; count: number }> } {
  const cellMap = new Map<string, number>();
  const typeMap = new Map<PingType, number>();

  for (const p of pings) {
    const local = p.ts_ms + p.tz_offset_min * 60 * 1000;
    const d = new Date(local);
    const hour = d.getUTCHours();
    const jsDay = d.getUTCDay();
    const dow = (jsDay + 6) % 7;
    const key = `${dow}_${hour}`;
    cellMap.set(key, (cellMap.get(key) ?? 0) + 1);
    typeMap.set(p.type, (typeMap.get(p.type) ?? 0) + 1);
  }

  const cells: HeatCell[] = [];
  for (let dow = 0; dow < 7; dow += 1) {
    for (let hour = 0; hour < 24; hour += 1) {
      const key = `${dow}_${hour}`;
      cells.push({ dow, hour, count: cellMap.get(key) ?? 0 });
    }
  }

  const topTypes = [...typeMap.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([type, count]) => ({ type, count }));

  return { cells, topTypes };
}

export function topTypesForBucket(pings: Ping[], dow: number, hour: number): Array<{ type: PingType; count: number }> {
  const counts = new Map<PingType, number>();
  for (const p of pings) {
    const local = p.ts_ms + p.tz_offset_min * 60 * 1000;
    const d = new Date(local);
    const pHour = d.getUTCHours();
    const pDow = (d.getUTCDay() + 6) % 7;
    if (pDow === dow && pHour === hour) {
      counts.set(p.type, (counts.get(p.type) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([type, count]) => ({ type, count }));
}

export function computeTransitions(pings: Ping[]): {
  top5: TransitionRow[];
  mostCommonPredecessor: Array<{ type: PingType; predecessor: PingType | null; count: number }>;
} {
  const sorted = [...pings].sort((a, b) => a.ts_ms - b.ts_ms);
  const transitionCounts = new Map<string, { count: number; gaps: number[] }>();
  const perTypePred = new Map<PingType, Map<PingType, number>>();

  for (let i = 1; i < sorted.length; i += 1) {
    const curr = sorted[i];
    const prev = sorted[i - 1];
    const dtMin = (curr.ts_ms - prev.ts_ms) / 60000;
    if (dtMin <= 0 || dtMin > W_LOOKBACK_MIN) continue;

    const key = `${prev.type}->${curr.type}`;
    const current = transitionCounts.get(key) ?? { count: 0, gaps: [] };
    current.count += 1;
    current.gaps.push(dtMin);
    transitionCounts.set(key, current);

    if (!perTypePred.has(curr.type)) perTypePred.set(curr.type, new Map());
    const predMap = perTypePred.get(curr.type)!;
    predMap.set(prev.type, (predMap.get(prev.type) ?? 0) + 1);
  }

  const top5 = [...transitionCounts.entries()]
    .map(([k, v]) => {
      const [from, to] = k.split('->') as [PingType, PingType];
      return {
        from,
        to,
        count: v.count,
        medianGapMin: median(v.gaps)
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const mostCommonPredecessor = FRICTION_TYPES.map(({ type }) => {
    const predMap = perTypePred.get(type);
    if (!predMap || predMap.size === 0) {
      return { type, predecessor: null, count: 0 };
    }
    const [predecessor, count] = [...predMap.entries()].sort((a, b) => b[1] - a[1])[0];
    return { type, predecessor, count };
  });

  return { top5, mostCommonPredecessor };
}

export function computeEffectiveness(pings: Ping[], rescues: RescueSession[]): RescueEffectiveness[] {
  const pingById = new Map(pings.map((p) => [p.id, p]));
  const sortedPings = [...pings].sort((a, b) => a.ts_ms - b.ts_ms);

  function nextPingTs(after: number): number | null {
    for (const p of sortedPings) {
      if (p.ts_ms > after) return p.ts_ms;
    }
    return null;
  }

  const byKind = new Map<RescueKind, RescueSession[]>();
  for (const r of rescues) {
    const arr = byKind.get(r.kind) ?? [];
    arr.push(r);
    byKind.set(r.kind, arr);
  }

  return RESCUES.map((rescue) => rescue.kind).map((kind) => {
    const items = byKind.get(kind) ?? [];
    const startedCount = items.length;
    const completed = items.filter((r) => r.completed === 1 && r.ts_end_ms !== null);
    const completedCount = completed.length;
    const withOutcome = completed.filter((r) => r.outcome !== null);
    const outcomeLoggedCount = withOutcome.length;
    const outcomeHelpedCount = withOutcome.filter((r) => r.outcome === 'helped' || r.outcome === 'a_bit').length;

    let resolvedCount = 0;
    const ttNext: number[] = [];
    for (const r of completed) {
      const ping = r.ping_id ? pingById.get(r.ping_id) : null;
      if (!ping || r.ts_end_ms === null) continue;
      const linkedWithin5 = r.ts_start_ms - ping.ts_ms <= R_LINK_MIN * 60 * 1000;
      if (!linkedWithin5) continue;
      const nextTs = nextPingTs(r.ts_end_ms);
      if (nextTs === null || nextTs - r.ts_end_ms > T_RESOLVE_MIN * 60 * 1000) {
        resolvedCount += 1;
      } else {
        ttNext.push((nextTs - r.ts_end_ms) / 60000);
      }
      if (nextTs === null) ttNext.push(BASELINE_WINDOW_MIN);
    }

    const completionRate = startedCount ? completedCount / startedCount : 0;
    const resolvedRate = completedCount ? resolvedCount / completedCount : 0;
    const helpfulRate = outcomeLoggedCount ? outcomeHelpedCount / outcomeLoggedCount : null;

    return {
      kind,
      startedCount,
      completedCount,
      completionRate,
      resolvedRate,
      outcomeLoggedCount,
      outcomeHelpedCount,
      helpfulRate,
      medianTimeToNextPingMin: ttNext.length ? median(ttNext) : null,
      smallSample: completedCount < 5
    };
  });
}

export function computeInterruptionSources(pings: Ping[]): Array<{ source: InterruptionSource; count: number }> {
  const counts = new Map<InterruptionSource, number>();
  for (const p of pings) {
    if (!p.interruption_source) continue;
    counts.set(p.interruption_source, (counts.get(p.interruption_source) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([source, count]) => ({ source, count }));
}

export function computeFrictionSubtypes(pings: Ping[]): Array<{ subtype: FrictionSubtype; count: number }> {
  const counts = new Map<FrictionSubtype, number>();
  for (const p of pings) {
    if (!p.subtype) continue;
    counts.set(p.subtype, (counts.get(p.subtype) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([subtype, count]) => ({ subtype, count }));
}

export function computeTinyWinSummary(rescues: RescueSession[]): TinyWinSummary {
  const completed = rescues.filter((r) => r.completed === 1);
  const withTinyWin = completed.filter((r) => (r.tiny_win ?? '').trim().length > 0);
  return {
    completedRescues: completed.length,
    withTinyWin: withTinyWin.length,
    captureRate: completed.length > 0 ? withTinyWin.length / completed.length : 0,
    latest: withTinyWin
      .sort((a, b) => b.ts_start_ms - a.ts_start_ms)
      .slice(0, 3)
      .map((r) => (r.tiny_win ?? '').trim())
  };
}

export function computePrepChecklistSummary(rescues: RescueSession[]): PrepChecklistSummary {
  const timed = rescues.filter((r) => r.duration_s > 0);
  if (timed.length === 0) {
    return {
      timedRescues: 0,
      phoneSilencedRate: 0,
      tabsClosedRate: 0,
      docOpenRate: 0,
      timerVisibleRate: 0
    };
  }

  const toRate = (pick: (r: RescueSession) => 0 | 1 | null): number =>
    timed.filter((r) => pick(r) === 1).length / timed.length;

  return {
    timedRescues: timed.length,
    phoneSilencedRate: toRate((r) => r.prep_phone_silenced),
    tabsClosedRate: toRate((r) => r.prep_tabs_closed),
    docOpenRate: toRate((r) => r.prep_doc_open),
    timerVisibleRate: toRate((r) => r.prep_timer_visible)
  };
}

export function computeFrictionStartSummary(pings: Ping[]): FrictionStartSummary {
  const leads = pings
    .map((p) => {
      if (p.friction_started_ms === null) return null;
      const diffMin = (p.ts_ms - p.friction_started_ms) / 60000;
      return diffMin > 0 ? diffMin : null;
    })
    .filter((x): x is number => x !== null);

  if (leads.length === 0) return { withStartCount: 0, medianLeadMin: null, meanLeadMin: null };
  const mean = leads.reduce((acc, x) => acc + x, 0) / leads.length;
  return {
    withStartCount: leads.length,
    medianLeadMin: median(leads),
    meanLeadMin: mean
  };
}
